import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Music, Sparkles, AlertCircle, RefreshCw, History } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import Header from '../../components/ui/Header';

import RoleSearch from './components/RoleSearch';
import RecommendationsDisplay from './components/RecommendationsDisplay';

export default function StudentAuditionSongsTool() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [selectedRole, setSelectedRole] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [aiServiceStatus, setAiServiceStatus] = useState(null);
  const [recommendationHistory, setRecommendationHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  // Advanced options
  const [auditionType, setAuditionType] = useState('general');
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  useEffect(() => {
    checkAIService();
    loadRecommendationHistory();
  }, [user]);

  const checkAIService = async () => {
    try {
      const { checkAIServiceAvailability } = await import('../../services/auditionSongService');
      const status = await checkAIServiceAvailability();
      setAiServiceStatus(status);
    } catch (err) {
      console.error('Error checking AI service:', err);
      setAiServiceStatus({
        available: false,
        reason: 'error',
        message: 'Failed to check AI service status'
      });
    }
  };

  const loadRecommendationHistory = async () => {
    try {
      const { getUserRecommendationHistory } = await import('../../services/auditionSongService');
      const history = await getUserRecommendationHistory(user?.id);
      setRecommendationHistory(history || []);
    } catch (err) {
      console.error('Error loading history:', err);
    }
  };

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    setRecommendations(null);
    setError('');
  };

  const handleGenerateRecommendations = async () => {
    if (!selectedRole) {
      setError('Please select a role first');
      return;
    }

    if (!aiServiceStatus?.available) {
      setError(aiServiceStatus?.message || 'AI service is currently unavailable');
      return;
    }

    setLoading(true);
    setError('');
    setRecommendations(null);

    try {
      const { getAuditionSongRecommendations } = await import('../../services/auditionSongService');
      
      const options = {
        auditionType,
        studentProfile: {
          // Future enhancement: Add student vocal profile
        }
      };

      const result = await getAuditionSongRecommendations(user?.id, selectedRole?.roleId, options);
      
      setRecommendations(result);
      
      // Reload history to include new recommendation
      await loadRecommendationHistory();
    } catch (err) {
      console.error('Error generating recommendations:', err);
      setError(err?.message || 'Failed to generate recommendations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadHistoricalRecommendation = (rec) => {
    setSelectedRole({
      roleId: rec?.roleId,
      characterName: rec?.characterName,
      musicalTitle: rec?.musicalTitle,
      musicalPoster: rec?.musicalPoster
    });
    setRecommendations({
      recommendations: rec?.recommendations,
      reasoning: rec?.reasoning,
      confidenceScore: rec?.confidenceScore,
      cached: true
    });
    setShowHistory(false);
  };

  return (
    <>
      <Helmet>
        <title>Audition Songs Tool - RoleCall Student Dashboard</title>
        <meta name="description" content="Get AI-powered audition song recommendations tailored to your chosen role and vocal profile" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-[#6e32b9] to-[#32215d]">
        <Header />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="bg-gradient-to-r from-purple-600/30 to-pink-600/30 backdrop-blur-md rounded-2xl p-6 mb-8 border border-purple-400/30">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-purple-500/30 rounded-xl">
                  <Music className="w-8 h-8 text-purple-200" />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                    Audition Songs Tool
                  </h1>
                  <p className="text-white/80 text-sm sm:text-base mb-4">
                    Search for a role and receive AI-powered song recommendations perfectly suited for your audition
                  </p>
                  
                  {/* AI Service Status */}
                  {aiServiceStatus && (
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                      aiServiceStatus?.available
                        ? 'bg-green-500/20 border border-green-500/30' :'bg-red-500/20 border border-red-500/30'
                    }`}>
                      <Sparkles className={`w-4 h-4 ${aiServiceStatus?.available ? 'text-green-400' : 'text-red-400'}`} />
                      <span className={`text-sm ${aiServiceStatus?.available ? 'text-green-200' : 'text-red-200'}`}>
                        AI Service: {aiServiceStatus?.message}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* History Button */}
              {recommendationHistory?.length > 0 && (
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                >
                  <History className="w-4 h-4" />
                  <span className="hidden sm:inline">History</span>
                </button>
              )}
            </div>
          </div>

          {/* History Panel */}
          {showHistory && recommendationHistory?.length > 0 && (
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-8 border border-white/20">
              <h2 className="text-xl font-bold text-white mb-4">Recent Recommendations</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {recommendationHistory?.map((rec) => (
                  <div
                    key={rec?.id}
                    onClick={() => loadHistoricalRecommendation(rec)}
                    className="flex gap-3 p-4 bg-white/5 hover:bg-white/10 rounded-xl cursor-pointer transition-all"
                  >
                    <img
                      src={rec?.musicalPoster || '/assets/images/no_image.png'}
                      alt={rec?.musicalTitle}
                      className="w-16 h-24 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <p className="text-white font-medium">{rec?.characterName}</p>
                      <p className="text-purple-300 text-sm">{rec?.musicalTitle}</p>
                      <p className="text-white/50 text-xs mt-1">
                        {new Date(rec?.createdAt)?.toLocaleDateString()}
                      </p>
                      <span className="inline-block mt-2 px-2 py-1 bg-purple-500/30 text-purple-200 rounded text-xs">
                        {rec?.recommendations?.length} songs
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column: Role Search */}
            <div>
              <RoleSearch
                onRoleSelect={handleRoleSelect}
                selectedRole={selectedRole}
              />

              {/* Advanced Options */}
              {selectedRole && (
                <div className="mt-6 bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                  <button
                    onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                    className="w-full flex items-center justify-between text-white font-medium mb-4"
                  >
                    <span>Advanced Options</span>
                    <RefreshCw className={`w-5 h-5 transition-transform ${showAdvancedOptions ? 'rotate-180' : ''}`} />
                  </button>

                  {showAdvancedOptions && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-white/70 text-sm mb-2">Audition Type</label>
                        <select
                          value={auditionType}
                          onChange={(e) => setAuditionType(e?.target?.value)}
                          className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-purple-400"
                        >
                          <option value="general" className="bg-gray-900">General Audition</option>
                          <option value="initial" className="bg-gray-900">Initial Audition</option>
                          <option value="callback" className="bg-gray-900">Callback</option>
                          <option value="competition" className="bg-gray-900">Competition</option>
                        </select>
                      </div>
                    </div>
                  )}

                  {/* Generate Button */}
                  <button
                    onClick={handleGenerateRecommendations}
                    disabled={loading || !aiServiceStatus?.available}
                    className="w-full mt-4 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <Sparkles className="w-5 h-5" />
                    {loading ? 'Generating...' : 'Generate AI Recommendations'}
                  </button>
                </div>
              )}
            </div>

            {/* Right Column: Recommendations */}
            <div>
              {error && (
                <div className="mb-6 bg-red-500/10 border border-red-500 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-red-200 text-sm">{error}</p>
                  </div>
                </div>
              )}

              <RecommendationsDisplay
                recommendations={recommendations?.recommendations}
                reasoning={recommendations?.reasoning}
                confidenceScore={recommendations?.confidenceScore}
                roleDetails={recommendations?.roleDetails}
                loading={loading}
                error={null}
              />
            </div>
          </div>

          {/* Educational Section */}
          <div className="mt-8 bg-gradient-to-r from-indigo-600/30 to-purple-600/30 backdrop-blur-md rounded-2xl p-6 border border-indigo-400/30">
            <h3 className="text-white font-bold text-lg mb-4">💡 Tips for Using Audition Songs Tool</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white/5 rounded-xl p-4">
                <h4 className="text-white font-medium mb-2">🎯 Choose Wisely</h4>
                <p className="text-white/70 text-sm">
                  Select roles that match your vocal range and type. The AI will provide better recommendations when the role aligns with your abilities.
                </p>
              </div>
              <div className="bg-white/5 rounded-xl p-4">
                <h4 className="text-white font-medium mb-2">🎭 Consider Context</h4>
                <p className="text-white/70 text-sm">
                  Different audition types require different approaches. Use the audition type filter to get targeted recommendations.
                </p>
              </div>
              <div className="bg-white/5 rounded-xl p-4">
                <h4 className="text-white font-medium mb-2">⭐ Trust the Process</h4>
                <p className="text-white/70 text-sm">
                  AI recommendations are based on extensive musical theater knowledge and consider vocal requirements, character fit, and audition effectiveness.
                </p>
              </div>
              <div className="bg-white/5 rounded-xl p-4">
                <h4 className="text-white font-medium mb-2">📚 Keep Learning</h4>
                <p className="text-white/70 text-sm">
                  Use these recommendations as a starting point. Research each suggested song to understand why it works for your audition.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}